import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClient } from "utils/client";
import { DocumentData } from "types";
import {
  DocumentDefinition,
  getEnhanceDocumentsForRevision,
} from "../hooks/useEnhancedDocuments";
import { requestDownload } from "pages/Project/hooks/useCreateProject";
import { zipDownload } from "components/FileExplorer/FileExplorer";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { RevisionSelector, RevisionInfo } from "./RevisionSelector";
import { DocumentGroupList } from "./DocumentGroup";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import { GenerateDocumentsModal } from "./GenerateDocumentsModal";
import { DocumentsDownloadLoader } from "./DocumentsDownloadLoader";
import { Modal } from "components/Modal/Modal";
import {
  DocumentTypeGroup,
  RevisionOption,
  DocumentLibraryProps,
} from "./types";
import { DocType } from "../types";
import { GeneratePDFParams } from "pages/Details/hooks/useProjectDetails";

export const DocumentLibrary = (props: DocumentLibraryProps) => {
  const client = useClient();
  const { t } = useTranslation();

  const [selectedRevision, setSelectedRevision] =
    useState<RevisionOption | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewLocation, setPreviewLocation] = useState<string>("");
  const [revisionOptions, setRevisionOptions] = useState<RevisionOption[]>([]);
  const [documentGroups, setDocumentGroups] = useState<DocumentTypeGroup[]>([]);
  const [isRevisionDropdownOpen, setIsRevisionDropdownOpen] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<DocType[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingModalVisible, setDownloadingModalVisible] = useState(false);

  // Initialize with project data
  useEffect(() => {
    if (!props.project) return;

    // Get all versions and create revision options
    const versions = props.project.versions || [];

    const versionOptions = versions
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .map((version) => ({
        label:
          `${version.version} - ${version.comment}` ||
          `Version ${version.version}`,
        date: new Date(version.createdAt).toLocaleDateString(),
        version: version.version,
        documentTypes: getUniqueDocTypes(version.documents || []).length,
        languages: getUniqueLanguages(version.documents || []).length,
      }));

    const allOptions = versionOptions;
    const latestVersion = allOptions[0];
    setRevisionOptions(allOptions);
    setSelectedRevision(latestVersion);

    // Process documents into groups
    const docs = getEnhanceDocumentsForRevision(
      props.project,
      props.project.versions?.find((v) => v.version === latestVersion.version)
        ?.documents || [],
    );

    processDocumentsIntoGroups(docs);
  }, [props.project]);

  // Get unique document types from document list
  const getUniqueDocTypes = (docs: DocumentData[] = []): string[] => {
    return [...new Set(docs.map((doc) => doc.type))];
  };

  // Get unique languages from document list
  const getUniqueLanguages = (docs: DocumentData[] = []): string[] => {
    return [
      ...new Set(docs.filter((doc) => doc.lang).map((doc) => doc.lang || "")),
    ];
  };

  // Process documents into type groups
  const processDocumentsIntoGroups = (docs: DocumentDefinition[] = []) => {
    const groupedByType: Record<string, DocumentDefinition[]> = {};

    // Group documents by type
    docs.forEach((doc) => {
      if (!groupedByType[doc.type]) {
        groupedByType[doc.type] = [];
      }
      groupedByType[doc.type].push(doc);
    });

    // Create document groups
    const groups = Object.entries(groupedByType)
      .map(([type, files]) => ({
        type,
        label: getDocumentTypeLabel(type),
        files: [...files].sort((a, b) =>
          (a.lang ?? "").localeCompare(b.lang ?? ""),
        ),
        version: "missing",
        expanded: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setDocumentGroups(groups);
  };

  // Get display label for document type
  const getDocumentTypeLabel = (type: string): string => {
    return t(`documents.${type}.label`) || type;
  };

  // Toggle expansion of a document group
  const toggleGroupExpansion = (index: number) => {
    const newGroups = [...documentGroups];
    newGroups[index].expanded = !newGroups[index].expanded;
    setDocumentGroups(newGroups);
  };

  // Handle revision selection
  const handleRevisionSelect = (revision: RevisionOption) => {
    setSelectedRevision(revision);
    setIsRevisionDropdownOpen(false);

    // Update document groups based on selected revision
    if (props.project) {
      const version = props.project.versions?.find(
        (v) => v.version === revision.version,
      )!;

      const docs = version.documents;
      const processableDocs = getEnhanceDocumentsForRevision(
        props.project,
        docs,
      );
      processDocumentsIntoGroups(processableDocs);
    }
  };

  const handlePreviewDocument = async (doc: DocumentData) => {
    const s3Location = await requestDownload(
      client,
      doc.key!,
      props.project?.projectdata.crn!,
    );

    setModalVisible(true);
    setPreviewLocation(s3Location);
  };

  const handleDownloadDocument = async (doc: DocumentData) => {
    const s3Location = await requestDownload(
      client,
      doc.key!,
      props.project?.projectdata.crn!,
    );

    doc.location = s3Location;

    zipDownload(
      [doc],
      props.project?.projectdata.crn!,
      props.project?.projectdata.product_name!,
    );
  };

  // Handle download all documents
  const handleDownloadAllDocuments = async () => {
    if (!props.project) return;

    // Show loading modal
    setIsDownloading(true);
    setDownloadingModalVisible(true);

    // Get all documents from the current version
    const allDocs = documentGroups.flatMap((group) => group.files);

    // Convert to DocumentData for zipDownload
    const downloadableDocs = allDocs.map(
      (doc) => doc as unknown as DocumentData,
    );

    try {
      for (const doc of downloadableDocs) {
        const s3Location = await requestDownload(
          client,
          doc.key!,
          props.project?.projectdata.crn!,
        );

        doc.location = s3Location;
      }

      // Download as zip
      zipDownload(
        downloadableDocs,
        props.project.projectdata.crn,
        props.project.projectdata.product_name,
      );
    } finally {
      // Hide loading modal
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadingModalVisible(false);
      }, 1000); // Small delay to ensure zip creation has started
    }
  };

  // Handle document selection
  useEffect(() => {
    // Get all document types from the groups
    const allDocs = documentGroups.flatMap((group) =>
      group.files.map((file) => file.type as DocType),
    );

    // Filter out duplicates
    const uniqueDocs = [...new Set(allDocs)];
    setSelectedDocs(uniqueDocs);
  }, [documentGroups]);

  // Handle generate documents
  const handleGenerateDocuments = async (params: GeneratePDFParams) => {
    // Call the onSelect callback to update selected documents in parent component
    if (props.onSelect) {
      props.onSelect(params.selectedDocs);
    }

    // Call the parent component's handler with all parameters
    if (props.onGenerate) {
      props.onGenerate({
        ...params,
        callback: () => {
          params.callback?.();
          setGenerateModalOpen(false);
        },
      });
    }
  };

  return (
    <div className="mt-4 pb-16">
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="pt-2 text-right space-x-2 flex items-center justify-end">
            {/* Buttons */}
            <div className="flex space-x-3">
              <PrimaryButton
                onClick={handleDownloadAllDocuments}
                disabled={
                  !props.project || documentGroups.length === 0 || isDownloading
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4 mr-1"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25"
                  />
                </svg>
                {isDownloading
                  ? t("documents.download.currently_downloading")
                  : t("documents.download.load_all")}
              </PrimaryButton>

              <PrimaryButton
                onClick={() => setGenerateModalOpen(true)}
                disabled={!props.project || props.isLoading}
              >
                {t("documents.download.generate_revision")}
              </PrimaryButton>
            </div>
          </div>

          <div className="mb-4">
            <div>
              {/* Revision Selector */}
              <RevisionSelector
                revisionOptions={revisionOptions}
                selectedRevision={selectedRevision}
                isRevisionDropdownOpen={isRevisionDropdownOpen}
                setIsRevisionDropdownOpen={setIsRevisionDropdownOpen}
                handleRevisionSelect={handleRevisionSelect}
              />

              {/* Revision Info */}
              <RevisionInfo selectedRevision={selectedRevision} />
            </div>
          </div>

          {/* Document Groups */}
          <DocumentGroupList
            documentGroups={documentGroups}
            toggleGroupExpansion={toggleGroupExpansion}
            handlePreviewDocument={handlePreviewDocument}
            handleDownloadDocument={handleDownloadDocument}
          />
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        previewLocation={previewLocation}
      />

      {/* Generate Documents Modal */}
      <GenerateDocumentsModal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onGenerate={handleGenerateDocuments}
        selectedDocs={selectedDocs}
        selectedLanguages={
          props.project?.languages?.map((lang) => lang.code) || []
        }
        projectName={props.project?.projectdata.product_name}
        project={props.project}
        previousRevisionKey={
          props.project?.versions?.find(
            (v) => selectedRevision && v.version === selectedRevision.version,
          )?.snapshotLocation
        }
      />

      {/* Download Loading Modal */}
      <Modal
        show={downloadingModalVisible}
        onClose={() => setDownloadingModalVisible(false)}
        content={
          <DocumentsDownloadLoader
            onComplete={() => {
              setIsDownloading(false);
              setDownloadingModalVisible(false);
            }}
          />
        }
        width="1/2"
      />
    </div>
  );
};

// Re-export from index.tsx for backward compatibility
export * from "./types";
